import { reducers, getState } from 'redux/core'
import cookie from 'js-cookie'
import request from 'sb-request'
import actions from 'redux/actions'
import { modifyItems } from 'redux/models/queue/api'
import { links, mixpanel, constants, getPrefixedAddressFields, getGqlQuery } from 'common/helpers'
import { callVindicia, setupBeginPayPal, setupCommitPayPal, setupBeginApplePay,
  setupCommitApplePay, syncUserAndSubscription, callAnalytics } from 'redux/models/payment/util'


const getSelectedProductIds = () => {
  const { selectedIds } = cookie.getJSON(constants.cookieNames.buyQueuePageData) || {}

  return selectedIds
}

const getAnalyticsData = () => {
  const {
    view: {
      redeemQueuePage: { queueProducts },
      paymentPageNew: { bill: { total }, inquire: { data: { discount } } },
    },
    models: { subscription: { currPlan: { productPrice } } },
  } = getState()

  const products = {
    ids: [],
    items: {},
  }

  queueProducts.forEach((product) => {
    const { id } = product

    products.ids.push(id)
    products.items[id] = {
      amount: 1,
      ...product,
    }
  })

  return {
    cart: {
      products,
      discount,
      totalPrice: total,
    },
    totalAmount: total,
    count: queueProducts.length,
  }
}

const paymentWrapper = (action) =>
  action
    .then((result) => syncUserAndSubscription({
      paymentType: constants.paymentTypes.ecommerce,
      ...getAnalyticsData(),
      ...result,
    }))
    .then(
      () => {
        cookie.remove(constants.cookieNames.buyQueuePageData)

        actions.models.queue.fetch()

        callAnalytics()

        actions.models.router.push(links.thankYou)

        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: true,
        })
      },
      (err) => {
        const { title, message, resData: { isCvvError, authCode } = {} } = err

        if (isCvvError) {
          actions.models.modals.open(constants.modals.ErrorCVV)
        }
        else {
          actions.models.modals.open(constants.modals.ServerError, {
            title,
            content: message,
          })
        }

        mixpanel.track(constants.analyticsEvents.paymentForm.placeOrder, {
          success: false,
          reason: `${authCode} | ${title} | ${message}`,
        })

        return Promise.reject(err)
      }
    )

const fetchCanBuyInOneClickStatus = () =>
  request.get('rest/order/fast')
    .then(({ canBuyInOneClick } = {}) => {
      reducers.view.redeemQueuePage.setCanBuyInOneClickStatus(canBuyInOneClick)
    })

const buyInOneClick = () => {
  const { models: { requestTokens: { order: token } } } = getState()

  const action = request.post('rest/order/fast', {
    body: {
      token,
      queueItemIds: getSelectedProductIds(),
    },
  })

  return paymentWrapper(action)
}


// ------------------------------------------------------------------------------ /

const fetchAvailableForQueueProducts = (queueProducts) => {
  const productIds = queueProducts.map(({ id }) => id)

  return request.post('graphql?name=buyQueueAvailableProducts', {
    body: {
      query: `{
        ${getGqlQuery('products', { includeProducts: productIds, availableForQueue: true })} {
          id
        }
      }`,
    },
    modifyResult: ({ products }) => products.map(({ id }) => id),
  })
    .then((availableProductIds) => (
      queueProducts.filter(({ id }) => availableProductIds.includes(id))
    ))
}

const fetchQueue = () => {
  reducers.view.redeemQueuePage.setQueueFetchingStatus(true)

  return request.post('graphql?name=buyQueue', {
    body: {
      query: `{
        queue(limit: 100) {
          items {
            status: statusName
            products {
              id
              name
              brand
              image
              category
              upchargePrice
              isLimitedEdition
              queueItemId
              price: priceWithBuyToQueueDiscount
            }
          }
        }
      }`,
    },
    modifyResult: ({ queue }) => modifyItems(queue),
  })
    .then(({ items }) => {
      const products = items.map(({ products }) => (
        products
          .filter(({ queueItemId, isUnavailable, isEmpty, disabled }) => (
            queueItemId && !disabled && !isUnavailable && !isEmpty
          ))
      ))
      const flatProducts = [].concat(...products)

      return fetchAvailableForQueueProducts(flatProducts)
    })
    .then((products) => reducers.view.redeemQueuePage.setQueueProducts(products))
    .finally(() => reducers.view.redeemQueuePage.setQueueFetchingStatus(false))
}

const fetchInquire = (values) => {
  let inquireRequest

  if (inquireRequest) {
    inquireRequest.abort()
  }

  inquireRequest = request.get('rest/order/inquire', {
    query: values,
    fixQuery: false,
    multiple: true,
    withErrorModal: false,
    withHumbleErrorModal: false,
    onComplete: () => {
      inquireRequest = null
    },
  })

  return inquireRequest
}

/**
 *
 * @param {Object} values
 * @param {array} values.queueItemIds
 */
const inquire = (values) => {
  reducers.view.redeemQueuePage.setInquireFetchingStatus(true)

  return fetchInquire(values)
    .then(({ price: total, ...rest }) => {
      reducers.view.redeemQueuePage.setInquireData({ total, ...rest })
    }, () => {
      reducers.view.redeemQueuePage.setInquireFetchingStatus(false)
    })
}


// ------------------------------------------------------------------------------ /


const begin = (values) => {
  const { models: { requestTokens: { order: token } } } = getState()
  const { shipping, billing, paymentMethod } = values

  const body = {
    ...getPrefixedAddressFields({ shipping, billing }),
    paymentType: paymentMethod,
    queueItemIds: getSelectedProductIds(),
    ignoreCredits: true,
    token,
  }

  return request.post('rest/order/begin', {
    body,
    tokenName: constants.requestTokens.order,
  })
}

const commit = (values) => {
  const body = {
    ...values,
    ignoreCredits: true,
  }

  return request.post('rest/order/commit', {
    body,
    tokenName: constants.requestTokens.order,
  })
}

const redeem = (values) => {
  const { shipping, billing, creditCard, paymentType } = values

  const action = begin(values)
    .then((data) => (
      callVindicia({
        paymentType,
        shipping,
        billing,
        creditCard,
        ...data,
      })
    ))
    .then(({ orderSession, token }) => commit({ orderSession, token }))

  return paymentWrapper(action)
}


// PayPal

const beginRedeemUsingPayPal = (values) => {
  const beginPayPal = setupBeginPayPal({
    begin,
    sessionName: 'orderSession',
  })

  return beginPayPal(values)
}

/**
 *
 * @param values
 * @param {string} values.paymentType
 * @param {string} values.paypalSessionId
 */
const commitRedeemUsingPayPal = (values) => {
  const commitPayPal = setupCommitPayPal({
    commit,
    sessionName: 'orderSession',
  })

  return commitPayPal(values)
    .then(
      () => {
        callAnalytics()
        actions.models.router.push(links.thankYou)
      },
      (err) => Promise.reject(err)
    )
}


// ApplePay

const beginRedeemUsingApplePay = (values) => {
  const beginApplePay = setupBeginApplePay({
    begin,
    tokenName: constants.requestTokens.order,
    sessionName: 'orderSession',
  })

  return beginApplePay(values)
}

const commitRedeemUsingApplePay = (session, values) => {
  const commitApplePay = setupCommitApplePay({
    commit,
    session,
    sessionName: 'orderSession',
    paymentType: constants.paymentTypes.ecommerce,
    tokenName: constants.requestTokens.order,
  })

  return commitApplePay(values)
    .then(
      () => {
        callAnalytics()
        actions.models.router.push(links.thankYou)
      },
      (err) => Promise.reject(err)
    )
}


export default {
  fetchCanBuyInOneClickStatus,
  fetchQueue,
  fetchInquire,
  inquire,
  redeem,
  buyInOneClick,
  beginRedeemUsingPayPal,
  commitRedeemUsingPayPal,
  beginRedeemUsingApplePay,
  commitRedeemUsingApplePay,
}
